import type { UniqueIdentifier } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useMergeRefs } from '@floating-ui/react';
import type { ReactElement, Ref } from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';
import DndContext from '../dragAndDrop/DndContext';
import SortableActiveItemOverlay from './SortableActiveItemOverlay';
import SortableItem from './SortableItem';
import useOptimisticReorder from './useOptimisticReorder';

interface BaseItem {
  id: UniqueIdentifier;
}

type Orientation = 'vertical' | 'horizontal';

type Props<T extends BaseItem> = {
  items: T[];
  /**
   * Called when an item is dropped in a new position, with the dragged item,
   * its destination index, and the reordered list. Return `false` to reject the
   * drop (e.g. failed validation) — the rendered order snaps back to `items`;
   * anything else (incl. `void`) commits.
   */
  onDragEnd: (
    activeItem: T,
    destinationIndex: number,
    items: T[]
  ) => boolean | void;
  /**
   * Renders an item. The element you return **becomes** the sortable item — it
   * is not wrapped. `SortableList` clones onto it the sortable node `ref`, the
   * drag `style` (transform), and `role="listitem"`.
   *
   * Therefore the returned root element:
   * - **must forward `ref` and `style`** to a host (DOM) element. A plain DOM
   *   element (`<div>`, `<li>`, …) does this automatically; a custom component
   *   must accept `& RefAttributes<HTMLElement>` and spread the remaining props
   *   onto its root element.
   * - **must not set its own `role`** — `role="listitem"` is applied for you,
   *   and setting another role (e.g. `role="button"`) clobbers the list
   *   semantics. To make a row clickable, render a `stretched-link` child rather
   *   than putting `role`/`onClick` on the root.
   */
  renderItem: (item: T) => ReactElement;
  /** Overrides the drag preview element; defaults to `renderItem`. */
  renderDragOverlay?: (item: T) => ReactElement;
  id: string;
  orientation?: Orientation;
  /**
   * When `true` (the default), items are dragged via a {@link DragHandle}
   * rendered somewhere inside `renderItem` — so include one for each item's id.
   * Set `false` to make the whole item draggable from anywhere (no `DragHandle`
   * required).
   */
  handle?: boolean;
  label?: string;
  labelledBy?: string;
  isDisabled?: boolean;
  className?: string;
  lockAxis?: boolean;
  hasKeyboardSensor?: boolean;
  ref?: Ref<HTMLDivElement>;
  emptyState?: ReactElement;
};

/**
 * A list whose items can be reordered by dragging. It manages the dnd-kit
 * `DndContext`, the optimistic reorder, and the drag overlay; you provide the
 * `items` and a `renderItem` for each one. Set `orientation="horizontal"` for a
 * horizontal list (defaults to `"vertical"`).
 *
 * The container is rendered with `role="list"` and each item with
 * `role="listitem"`. See {@link Props.renderItem} for the contract this places
 * on the element you return, and {@link Props.handle} for drag-handle usage.
 */
const SortableList = <T extends BaseItem>(props: Props<T>) => {
  const {
    items,
    id,
    orientation = 'vertical',
    handle = true,
    label,
    labelledBy,
    isDisabled = false,
    lockAxis = false,
    hasKeyboardSensor,
    className,
    renderItem,
    renderDragOverlay,
    ref,
    emptyState,
  } = props;

  const isHorizontal = orientation === 'horizontal';
  const { setNodeRef } = useDroppable({ id });
  const mergedRef = useMergeRefs([setNodeRef, ref]);

  const { displayItems, commit } = useOptimisticReorder(items);

  return (
    <DndContext
      sortable
      hasKeyboardSensor={hasKeyboardSensor}
      modifiers={
        lockAxis
          ? [isHorizontal ? restrictToHorizontalAxis : restrictToVerticalAxis]
          : undefined
      }
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const activeIndex = displayItems.findIndex(
          ({ id }) => id === active.id
        );
        const overIndex = displayItems.findIndex(({ id }) => id === over.id);
        const newItems = arrayMove(displayItems, activeIndex, overIndex);
        const result = props.onDragEnd(
          displayItems[activeIndex],
          overIndex,
          newItems
        );
        if (result !== false) commit(newItems);
      }}
    >
      <SortableContext
        items={displayItems}
        strategy={
          isHorizontal ? horizontalListSortingStrategy : rectSortingStrategy
        }
      >
        {displayItems.length === 0 && emptyState ? (
          emptyState
        ) : (
          <div
            ref={mergedRef}
            role="list"
            className={clstx(isHorizontal && 'flex', className)}
            aria-label={label}
            aria-labelledby={labelledBy}
          >
            {displayItems.map((item) => (
              <SortableItem
                key={item.id}
                item={item}
                renderItem={renderItem}
                isDisabled={isDisabled}
                handle={handle}
              />
            ))}
          </div>
        )}
      </SortableContext>

      <SortableActiveItemOverlay
        items={displayItems}
        renderItem={renderDragOverlay ?? renderItem}
      />
    </DndContext>
  );
};

export default SortableList;
